import React, { useState, useEffect } from 'react';
import creditPricingService from '../services/creditPricingService';
import './CreditPricing.css';

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M11.333 2a1.414 1.414 0 1 1 2 2L5.333 13.333 2 14l.667-3.333L11.333 2z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
  </svg>
);

const DeleteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M2 4h12M12.667 4v9.333a.667.667 0 01-.667.667H4a.667.667 0 01-.667-.667V4m2 0V2.667A.667.667 0 016 2h4a.667.667 0 01.667.667V4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
  </svg>
);

const CreditPricing = () => {
  const [configs, setConfigs] = useState([]);
  const [activeConfig, setActiveConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Drawer states
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('add');
  const [currentEditId, setCurrentEditId] = useState(null);
  const [formData, setFormData] = useState({
    config_name: '',
    input_token_price_per_million: 0.15,
    output_token_price_per_million: 0.60,
    image_generation_price_per_image: 0.04,
    markup_percentage: 100.00,
    notes: '',
    set_as_active: false
  });

  // Delete modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  // Load configs on mount
  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await creditPricingService.getAll();
      if (response.success) {
        setConfigs(response.configs);
        const active = response.configs.find(c => c.is_active);
        setActiveConfig(active || null);
      }
    } catch (err) {
      console.error('Error loading credit pricing configs:', err);
      setError('Failed to load credit pricing configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setFormData({
      config_name: '',
      input_token_price_per_million: 0.15,
      output_token_price_per_million: 0.60,
      image_generation_price_per_image: 0.04,
      markup_percentage: 100.00,
      notes: '',
      set_as_active: false
    });
    setDrawerMode('add');
    setCurrentEditId(null);
    setIsDrawerOpen(true);
  };

  const handleEdit = (config) => {
    setFormData({
      config_name: config.config_name,
      input_token_price_per_million: config.input_token_price_per_million,
      output_token_price_per_million: config.output_token_price_per_million,
      image_generation_price_per_image: config.image_generation_price_per_image,
      markup_percentage: config.markup_percentage,
      notes: config.notes || '',
      set_as_active: config.is_active
    });
    setDrawerMode('edit');
    setCurrentEditId(config.id);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setFormData({
      config_name: '',
      input_token_price_per_million: 0.15,
      output_token_price_per_million: 0.60,
      image_generation_price_per_image: 0.04,
      markup_percentage: 100.00,
      notes: '',
      set_as_active: false
    });
    setCurrentEditId(null);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    try {
      const submitData = {
        config_name: formData.config_name,
        input_token_price_per_million: parseFloat(formData.input_token_price_per_million),
        output_token_price_per_million: parseFloat(formData.output_token_price_per_million),
        image_generation_price_per_image: parseFloat(formData.image_generation_price_per_image),
        markup_percentage: parseFloat(formData.markup_percentage),
        notes: formData.notes.trim() || null,
        set_as_active: formData.set_as_active
      };

      if (drawerMode === 'add') {
        await creditPricingService.create(submitData);
      } else {
        await creditPricingService.update(currentEditId, submitData);
      }

      await loadConfigs();
      handleCloseDrawer();

    } catch (err) {
      console.error('Error saving credit pricing config:', err);
      if (err.response?.data?.error || err.response?.data?.message) {
        alert(err.response.data.error || err.response.data.message);
      } else {
        alert('Failed to save credit pricing configuration. Please try again.');
      }
    }
  };

  const handleDelete = (id) => {
    setDeleteTargetId(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await creditPricingService.delete(deleteTargetId);
      await loadConfigs();
      setIsDeleteModalOpen(false);
      setDeleteTargetId(null);
    } catch (err) {
      console.error('Error deleting credit pricing config:', err);
      if (err.response?.data?.error || err.response?.data?.message) {
        alert(err.response.data.error || err.response.data.message);
      } else {
        alert('Failed to delete credit pricing configuration. Please try again.');
      }
      setIsDeleteModalOpen(false);
      setDeleteTargetId(null);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setDeleteTargetId(null);
  };

  const handleActivate = async (id) => {
    try {
      await creditPricingService.activate(id);
      await loadConfigs();
    } catch (err) {
      console.error('Error activating credit pricing config:', err);
      if (err.response?.data?.error || err.response?.data?.message) {
        alert(err.response.data.error || err.response.data.message);
      } else {
        alert('Failed to activate credit pricing configuration. Please try again.');
      }
    }
  };

  // Calculate example cost
  const calculateExampleCost = () => {
    const inputTokens = 5000;
    const outputTokens = 3000;
    const images = 5;

    const inputCost = (inputTokens / 1000000) * (formData.input_token_price_per_million || 0);
    const outputCost = (outputTokens / 1000000) * (formData.output_token_price_per_million || 0);
    const imageCost = images * (formData.image_generation_price_per_image || 0);
    const baseCost = inputCost + outputCost + imageCost;
    const markupAmount = baseCost * ((formData.markup_percentage || 0) / 100);
    const finalCost = baseCost + markupAmount;

    return { inputCost, outputCost, imageCost, baseCost, markupAmount, finalCost };
  };

  const exampleCost = calculateExampleCost();

  if (loading && configs.length === 0) {
    return (
      <div className="credit-pricing-page">
        <div className="loading-container">Loading...</div>
      </div>
    );
  }

  return (
    <div className="credit-pricing-page">
      <div className="credit-pricing-header">
        <div>
          <h1>Credit Pricing Configuration</h1>
          <p>Manage pricing for input tokens, output tokens, and image generation</p>
        </div>
        <button className="add-button" onClick={handleAddNew}>
          + Create New Configuration
        </button>
      </div>

      {activeConfig && (
        <div className="active-config-banner">
          <div className="active-config-content">
            <div>
              <strong>Active Configuration:</strong> {activeConfig.config_name}
            </div>
            <div className="active-config-details">
              Input: ${activeConfig.input_token_price_per_million}/M | 
              Output: ${activeConfig.output_token_price_per_million}/M | 
              Image: ${activeConfig.image_generation_price_per_image} | 
              Markup: {activeConfig.markup_percentage}%
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="error-message">{error}</div>
      )}

      <div className="configs-table-container">
        <table className="configs-table">
          <thead>
            <tr>
              <th>Config Name</th>
              <th>Input Token Price</th>
              <th>Output Token Price</th>
              <th>Image Price</th>
              <th>Markup %</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {configs.length === 0 ? (
              <tr>
                <td colSpan="7" className="empty-state">
                  No pricing configurations found. Create one to get started.
                </td>
              </tr>
            ) : (
              configs.map(config => (
                <tr key={config.id} className={config.is_active ? 'active-row' : ''}>
                  <td>
                    <strong>{config.config_name}</strong>
                    {config.notes && (
                      <div className="config-notes">{config.notes}</div>
                    )}
                  </td>
                  <td>${config.input_token_price_per_million.toFixed(6)}/M</td>
                  <td>${config.output_token_price_per_million.toFixed(6)}/M</td>
                  <td>${config.image_generation_price_per_image.toFixed(4)}</td>
                  <td>{config.markup_percentage}%</td>
                  <td>
                    {config.is_active ? (
                      <span className="status-badge active">Active</span>
                    ) : (
                      <span className="status-badge inactive">Inactive</span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      {!config.is_active && (
                        <button
                          className="action-button activate"
                          onClick={() => handleActivate(config.id)}
                          title="Activate"
                        >
                          Activate
                        </button>
                      )}
                      <button
                        className="action-button edit"
                        onClick={() => handleEdit(config)}
                        title="Edit"
                      >
                        <EditIcon />
                      </button>
                      {!config.is_active && (
                        <button
                          className="action-button delete"
                          onClick={() => handleDelete(config.id)}
                          title="Delete"
                        >
                          <DeleteIcon />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Drawer for Add/Edit */}
      {isDrawerOpen && (
        <>
          <div className="drawer-overlay" onClick={handleCloseDrawer}></div>
          <div className="drawer">
            <div className="drawer-header">
              <h2>{drawerMode === 'add' ? 'Create' : 'Edit'} Credit Pricing Configuration</h2>
              <button className="drawer-close" onClick={handleCloseDrawer}>
                <CloseIcon />
              </button>
            </div>

            <form onSubmit={handleFormSubmit} className="drawer-form">
              <div className="form-group">
                <label htmlFor="config_name">
                  Config Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="config_name"
                  value={formData.config_name}
                  onChange={(e) => setFormData({ ...formData, config_name: e.target.value })}
                  required
                  placeholder="e.g., Current Pricing, GPT-4o-mini Pricing"
                />
              </div>

              <div className="form-group">
                <label htmlFor="input_token_price_per_million">
                  Input Token Price (per million tokens) <span className="required">*</span>
                </label>
                <input
                  type="number"
                  id="input_token_price_per_million"
                  step="0.000001"
                  min="0"
                  value={formData.input_token_price_per_million}
                  onChange={(e) => setFormData({ ...formData, input_token_price_per_million: e.target.value })}
                  required
                />
                <small>OpenAI charges $0.15 per million input tokens for GPT-4o-mini</small>
              </div>

              <div className="form-group">
                <label htmlFor="output_token_price_per_million">
                  Output Token Price (per million tokens) <span className="required">*</span>
                </label>
                <input
                  type="number"
                  id="output_token_price_per_million"
                  step="0.000001"
                  min="0"
                  value={formData.output_token_price_per_million}
                  onChange={(e) => setFormData({ ...formData, output_token_price_per_million: e.target.value })}
                  required
                />
                <small>OpenAI charges $0.60 per million output tokens for GPT-4o-mini</small>
              </div>

              <div className="form-group">
                <label htmlFor="image_generation_price_per_image">
                  Image Generation Price (per image) <span className="required">*</span>
                </label>
                <input
                  type="number"
                  id="image_generation_price_per_image"
                  step="0.0001"
                  min="0"
                  value={formData.image_generation_price_per_image}
                  onChange={(e) => setFormData({ ...formData, image_generation_price_per_image: e.target.value })}
                  required
                />
                <small>OpenAI charges $0.04 per image for DALL-E 3 (1024×1024)</small>
              </div>

              <div className="form-group">
                <label htmlFor="markup_percentage">
                  Markup Percentage (%) <span className="required">*</span>
                </label>
                <input
                  type="number"
                  id="markup_percentage"
                  step="0.01"
                  min="0"
                  max="1000"
                  value={formData.markup_percentage}
                  onChange={(e) => setFormData({ ...formData, markup_percentage: e.target.value })}
                  required
                />
                <small>Percentage markup to add to base cost (100% = double the price)</small>
              </div>

              <div className="form-group">
                <label htmlFor="notes">Notes</label>
                <textarea
                  id="notes"
                  rows="3"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Internal notes for reference..."
                />
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.set_as_active}
                    onChange={(e) => setFormData({ ...formData, set_as_active: e.target.checked })}
                  />
                  Set this configuration as active
                </label>
                <small>Only one configuration can be active. Activating this will deactivate the current active configuration.</small>
              </div>

              {/* Example Calculation Preview */}
              <div className="example-calculation">
                <h3>Example Calculation</h3>
                <div className="calculation-details">
                  <div>Input Tokens: 5,000</div>
                  <div>Output Tokens: 3,000</div>
                  <div>Images: 5</div>
                  <div className="calculation-divider"></div>
                  <div>Input Cost: ${exampleCost.inputCost.toFixed(6)}</div>
                  <div>Output Cost: ${exampleCost.outputCost.toFixed(6)}</div>
                  <div>Image Cost: ${exampleCost.imageCost.toFixed(4)}</div>
                  <div><strong>Base Cost: ${exampleCost.baseCost.toFixed(4)}</strong></div>
                  <div>Markup ({formData.markup_percentage}%): ${exampleCost.markupAmount.toFixed(4)}</div>
                  <div className="calculation-final"><strong>Final Cost to User: ${exampleCost.finalCost.toFixed(4)}</strong></div>
                </div>
              </div>

              <div className="drawer-actions">
                <button type="button" className="cancel-button" onClick={handleCloseDrawer}>
                  Cancel
                </button>
                <button type="submit" className="save-button">
                  {drawerMode === 'add' ? 'Create' : 'Update'} Configuration
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <>
          <div className="modal-overlay" onClick={handleCancelDelete}></div>
          <div className="modal">
            <h2>Delete Credit Pricing Configuration</h2>
            <p>Are you sure you want to delete this pricing configuration? This action cannot be undone.</p>
            <div className="modal-actions">
              <button className="cancel-button" onClick={handleCancelDelete}>
                Cancel
              </button>
              <button className="delete-button" onClick={handleConfirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CreditPricing;

